/**
 * Cards over the well: the correct answer after a miss (a learning moment,
 * never a telling-off), the level banner while the new theme sweeps in, and
 * the gentle game-over card.
 */
import { memo, useMemo } from 'react';
import type { CheckpointResult, Locale, Problem, ResolutionKind } from '@dropstack/core';
import { useT } from '../i18n/index.ts';
import { themeForLevel, type LevelChangeTone } from './announce.ts';
import { MathNodes } from './MathText.tsx';
import { answerReveal } from './mathView.ts';

interface RevealProps {
  problem: Problem;
  locale: Locale;
}

/** "The answer is x = 4" (or the prompt with its box filled in). */
const RevealLine = memo(function RevealLine({ problem, locale }: RevealProps) {
  const t = useT();
  const reveal = useMemo(() => answerReveal(problem, locale), [problem, locale]);
  return (
    <div className="game-card__reveal">
      <span className="game-card__label">{t('game.answerIs')}</span>
      <MathNodes nodes={reveal.view} label={reveal.speech} className="game-card__math" />
    </div>
  );
});

export interface AnswerCardProps extends RevealProps {
  kind: Exclude<ResolutionKind, 'correct'>;
  compact?: boolean;
}

export function AnswerCard({ kind, problem, locale, compact = false }: AnswerCardProps) {
  const t = useT();
  return (
    <div className={`game-card game-card--answer${compact ? ' game-card--compact' : ''}`}>
      <p className="game-card__title">{t(kind === 'wrong' ? 'game.wrongTitle' : 'game.timeoutTitle')}</p>
      <RevealLine problem={problem} locale={locale} />
      {!compact && <p className="game-card__hint">{t('game.encourage')}</p>}
    </div>
  );
}

export interface LevelBannerProps {
  change: CheckpointResult;
  /** How to frame it (announce.ts levelChangeTone). */
  tone: LevelChangeTone;
  locale: Locale;
  compact?: boolean;
}

/**
 * "Level 7 — Coral Lagoon unlocked!" — or, going down, a calm "let's practise
 * here", and after a jump that didn't stick a kind "let's step back a level".
 */
export function LevelBanner({ change, tone, locale, compact = false }: LevelBannerProps) {
  const t = useT();
  const theme = themeForLevel(change.levelAfter);
  const name = theme.name[locale];
  const title =
    tone === 'up' ? t('game.levelUnlocked', { theme: name }) : tone === 'down' ? t('game.levelBack', { theme: name }) : t('game.levelStepBack');
  const hint = tone === 'stepBack' ? t('game.levelStepBackHint', { theme: name }) : theme.tagline[locale];
  return (
    <div className={`game-card game-card--level game-card--${tone === 'up' ? 'up' : 'down'}${compact ? ' game-card--compact' : ''}`}>
      <span
        className="game-card__swatch"
        aria-hidden="true"
        style={{ background: `linear-gradient(${theme.palette.skyTop}, ${theme.palette.skyBottom})` }}
      />
      <p className="game-card__eyebrow">{t('game.levelTitle', { level: change.levelAfter })}</p>
      <p className="game-card__title">{title}</p>
      {!compact && <p className="game-card__hint">{hint}</p>}
    </div>
  );
}

export interface GameOverCardProps {
  /** The miss that ended the run, to show its answer too. */
  missed: RevealProps | null;
  compact?: boolean;
}

export function GameOverCard({ missed, compact = false }: GameOverCardProps) {
  const t = useT();
  return (
    <div className={`game-card game-card--over${compact ? ' game-card--compact' : ''}`}>
      <p className="game-card__title">{t('game.gameOverTitle')}</p>
      {missed && <RevealLine problem={missed.problem} locale={missed.locale} />}
      {!compact && <p className="game-card__hint">{t('game.gameOverText')}</p>}
    </div>
  );
}
